import { z } from 'zod';
import { AvatarSchema } from '../../shared/avatar.schema';
import { PopulatedSystemSchema } from '../../entities/system';
import { PopulatedExternalContactSchema } from '../../entities/external-contact';
import { OrgFunctionKindSchema } from '../../entities/org-function/org-function-ref.schema';
import { ProcessStatusSchema } from '../../entities/process';
import { SurveyStatusSchema } from '../../entities/survey/survey.schema';
// ── Owner / Assignee (lightweight user ref) ─────────────────────────
const UserRefSchema = z.object({
    _id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    avatar: AvatarSchema.pick({
        thumbnail: true,
        medium: true
    }).nullable().optional(),
    role: z.object({
        _id: z.string(),
        name: z.string()
    }).nullable().optional(),
    departmentAbbreviation: z.string().nullable().optional()
});
const SystemRefSchema = PopulatedSystemSchema.pick({
    _id: true,
    name: true,
    category: true,
    logo: true
});
// OrgFunction ref flattens `department` → `departmentAbbreviation`, like UserRefSchema.
// Carries `kind` so the dashboard can distinguish team- from role-kind assignees.
const OrgFunctionRefSchema = z.object({
    _id: z.string(),
    name: z.string(),
    kind: OrgFunctionKindSchema,
    email: z.string().optional(),
    departmentAbbreviation: z.string().nullable().optional()
});
const ExternalContactRefSchema = PopulatedExternalContactSchema.pick({
    _id: true,
    name: true,
    type: true
});
export const AssigneeRefSchema = z.discriminatedUnion('model', [
    z.object({
        model: z.literal('User'),
        user: UserRefSchema
    }),
    z.object({
        model: z.literal('System'),
        system: SystemRefSchema
    }),
    z.object({
        model: z.literal('OrgFunction'),
        orgFunction: OrgFunctionRefSchema
    }),
    z.object({
        model: z.literal('ExternalContact'),
        contact: ExternalContactRefSchema
    })
]);
// ── Not-Started Process ─────────────────────────────────────────────
export const NotStartedProcessSchema = z.object({
    _id: z.string(),
    name: z.string(),
    owner: UserRefSchema,
    isOverdue: z.boolean(),
    dueDate: z.number(),
    canNudge: z.boolean(),
    canStart: z.boolean()
});
// ── In-Recording Flat Step ──────────────────────────────────────────
export const RecordingDashboardFlatStepSchema = z.object({
    _id: z.string(),
    recordingId: z.string().optional(),
    assignmentId: z.string().optional(),
    stepName: z.string(),
    processId: z.string(),
    processName: z.string(),
    // Per-step copy of the parent process's verdict, like `processName`: the
    // cockpit groups these flat rows back into process rows on the client.
    processStatus: ProcessStatusSchema,
    // Per-step copy of the parent process's nudge verdict, like `processStatus`.
    canNudge: z.boolean(),
    owner: UserRefSchema,
    assignee: AssigneeRefSchema.nullable(),
    isCompleted: z.boolean(),
    // True when this step is anchored at the process's start event: its
    // recording/assignment source is a start event node id (issue 11) or the
    // legacy 'START' marker (only M2's orphan-repair rows still carry it).
    // Drives start-event-only UI rules — e.g. hiding "External contact" when
    // reassigning a start step.
    isStartStep: z.boolean(),
    isOverdue: z.boolean(),
    dueDate: z.number().optional()
});
// ── Survey ──────────────────────────────────────────────────────────
// A dismissed row (ADR 0022) carries no `dueDate`: nothing is owed.
export const RecordingDashboardSurveySchema = z.object({
    _id: z.string(),
    processId: z.string(),
    processName: z.string(),
    stepName: z.string(),
    user: UserRefSchema,
    status: SurveyStatusSchema,
    dueDate: z.number().optional(),
    canNudge: z.boolean()
});
// ── Summary ─────────────────────────────────────────────────────────
export const RecordingDashboardSummarySchema = z.object({
    completedCount: z.number(),
    // Processes in the In Aufnahme tab: started minus completed. A subset of
    // them — recording done, surveys still open — is `recordingCompleteCount`.
    inRecordingCount: z.number(),
    recordingCompleteCount: z.number(),
    notStartedCount: z.number(),
    totalCount: z.number(),
    pendingSurveyCount: z.number(),
    completedSurveyCount: z.number()
});
// ── Top-level Response ──────────────────────────────────────────────
export const ShowRecordingDashboardResponseSchema = z.object({
    summary: RecordingDashboardSummarySchema,
    notStartedProcesses: z.array(NotStartedProcessSchema),
    inRecordingSteps: z.array(RecordingDashboardFlatStepSchema),
    surveys: z.array(RecordingDashboardSurveySchema),
    completedSteps: z.array(RecordingDashboardFlatStepSchema),
    completedSurveys: z.array(RecordingDashboardSurveySchema)
});
export const ShowRecordingDashboardRequestSchema = z.void();

//# sourceMappingURL=show-recording-dashboard.schema.js.map